// @flow

import React, { useContext, type AbstractComponent, type Node } from 'react';
import { type RequestContext as _RequestContextType } from 'app/common/lib/RequestContext';

export { RequestContextPropType } from 'app/common/lib/RequestContext';

// Re-export the context types here. This makes it so consumers only need to import one file.
export type RequestContextType = _RequestContextType;

// Create the React context with static factory values as fallback. The consumer methods below
// will complain if there's no provider in the React tree.
const context = React.createContext<?RequestContextType>();

// Provider
export const RequestContextProvider = context.Provider;

// HOC
type InjectedProps = { requestContext: RequestContextType };
export function withRequestContext<OwnProps: InjectedProps>(
  Subject: AbstractComponent<OwnProps>,
): AbstractComponent<$Diff<OwnProps, InjectedProps>> {
  function wrapper(props: $Diff<OwnProps, InjectedProps>) {
    const requestContext = useContext(context); // eslint-disable-line react-hooks/rules-of-hooks

    if (!requestContext) {
      throw new Error('withRequestContext must be used within a RequestContextProvider');
    }

    return <Subject {...props} requestContext={requestContext} />;
  }

  wrapper.displayName = `withRequestContext(${String(Subject.displayName || Subject.name)})`;

  return wrapper;
}

// Render Prop
export const RequestContext = ({ children }: { children: (RequestContextType) => Node }) => {
  const requestContext = useContext(context);

  if (!requestContext) {
    throw new Error('RequestContext must be used within a RequestContextProvider');
  }

  return children(requestContext);
};

// Hook
export function useRequestContext() {
  const requestContext = React.useContext(context);

  if (!requestContext) {
    throw new Error('useRequestContext must be used within a RequestContextProvider');
  }

  return requestContext;
}
